import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';
import type { CheckoutOptions } from 'react-native-razorpay';

import {
  NETWORK_MESSAGE,
  PSErrorClass,
  UNKNOWN_MESSAGE,
} from '@/src/api/apiError';
import type { BookingPayment } from '@/src/api/bookingsApi';
import type {
  MembershipCheckout,
  OrderCheckout,
  SubscriptionCheckout,
} from '@/src/api/membershipsApi';

/**
 * ── The Razorpay Checkout sheet, behind one function ─────────────────────
 *
 * `openPaymentSheet` takes the `payment` block the server bolted onto the
 * booking, opens the native sheet with it, and answers ONE question: should we
 * go and ask our own backend what happened, or not?
 *
 * That's the whole contract, and it's deliberately not "did the payment
 * succeed". The SDK's success callback isn't proof of payment — the booking is
 * confirmed when Razorpay's capture webhook reaches OUR server, which can happen
 * before or after the user is back in the app. So the resolved payment id is
 * ignored, and [[usePollBooking]] remains the only thing that decides a booking
 * is paid.
 *
 * ── The three outcomes ──────────────────────────────────────────────────
 *   `submitted`   the user went through with it, or it's genuinely unresolved →
 *                 hand off to the poll.
 *   `cancelled`   they backed out. NOT an error: nothing failed, so nothing is
 *                 thrown and the screen says so gently.
 *   THROWS        a `PSErrorClass`, so a caller's existing `catch` treats a
 *                 payment failure exactly like an API failure.
 *
 * ── Three platforms, one contract ───────────────────────────────────────
 * Web has no native module, so `metro.config.js` aliases the package to
 * [[razorpay.web]], which drives Razorpay's `checkout.js` and rejects in the
 * shape read below. Everything above this line is true on all three platforms;
 * only the reading of a rejection differs, and that lives in `isCancellation`
 * and `toPaymentError`.
 *
 * ── Why this file is 200 lines instead of a try/catch ───────────────────
 * Because the caught value is nothing like what the types claim. Verified in the
 * installed package's own source:
 *
 *   - It's a PLAIN OBJECT off the native bridge, not an Error
 *     (`RazorpayCheckout.js:21-24` rejects with the event body). No `message`,
 *     no `stack`, and `instanceof` tells you nothing.
 *   - The envelope is at a DIFFERENT KEY per platform: `error` on Android
 *     (`RazorpayModule.java:91-101`), `details.error` on iOS
 *     (`RazorpayEventEmitter.m:73-80`). `@types/react-native-razorpay` declares
 *     only the Android one, so following the types loses every iOS detail.
 *   - The numeric `code` MEANS DIFFERENT THINGS per platform. iOS: 0 network,
 *     1 init failure, 2 cancelled. Android: 0 cancelled, 2 network, 3 invalid
 *     options, 6 TLS. `code === 2 ⇒ cancelled` is right on iOS and reports a
 *     network blip as a cancellation on Android.
 *   - Android's TOP-LEVEL `description` is the raw JSON envelope as a string,
 *     and elsewhere it's `net::ERR_*`, `"BackPressed"`, `"TLSv1  is not
 *     supported for security reasons"`, or a raw Java exception message. It is
 *     never user-facing. Only the NESTED `description` is written for customers.
 *
 * So: read the envelope from either key, branch on `reason` (which Razorpay
 * documents as the field to handle programmatically and which means the same
 * thing on both platforms), and fall back to the numeric code only when there's
 * no envelope at all.
 *
 * ── A known gap we can't close from here ────────────────────────────────
 * `RazorpayModule.open()` swallows its own exceptions (`catch (Exception e) {}`,
 * line 66) — a null activity or a throw emits no event, so the promise NEVER
 * settles and the caller's button stays in flight forever. A timeout is not the
 * fix: the user may legitimately sit on the sheet for minutes, so any timer long
 * enough to be safe is too long to help, and any timer short enough to help
 * would abandon real payments. It needs a `getCurrentActivity()` null check in
 * the library. Reachable only if the sheet is opened from an unmounted screen.
 */

/** What the sheet resolved to. Failures throw instead — see the docstring. */
export type CheckoutOutcome = { status: 'submitted' } | { status: 'cancelled' };

/**
 * `reason` values that mean the outcome is genuinely NOT KNOWN YET — a UPI
 * collect still sitting in the user's app, a bank that hasn't answered, a
 * "deemed" transaction pending reconciliation.
 *
 * These resolve as `submitted`, not as errors, because our server is the one
 * that finds out: the poll either sees the capture land or times out with
 * "we're still confirming". Telling someone their payment failed while the money
 * may already have left their account is the most expensive wrong sentence on
 * this screen.
 */
const UNRESOLVED_REASONS = new Set([
  'payment_pending',
  'payment_pending_approval',
  'collect_request_pending',
  'deemed_transaction',
]);

/** The merchant line the sheet prints when the server sends no name of its own. */
const MERCHANT_NAME = 'Putting Scene';

/**
 * Open the sheet for a booking's `payment` block.
 *
 * The options are the server's values, renamed to the SDK's snake_case by hand —
 * [[api]]'s transform doesn't reach here, since this isn't an HTTP call.
 */
export function openPaymentSheet(
  payment: BookingPayment
): Promise<CheckoutOutcome> {
  return openSheet({
    key: payment.key,
    order_id: payment.orderId,
    amount: payment.amount,
    currency: payment.currency,
    name: payment.name,
    // Required by the SDK's types and shown under the merchant name in the
    // sheet. The server sends no copy for it — `payment.name` is the event —
    // so this is ours.
    description: 'Event tickets',
    prefill: payment.prefill,
  });
}

/**
 * What a membership sheet needs on top of the server's `checkout` block: the
 * merchant name is ours because the server sends none, and so is the prefill.
 */
type MembershipSheet<Checkout> = {
  checkout: Checkout;
  /** The plan's name — the description line under the merchant. */
  planName: string;
  payer: { name: string; email: string; contact: string };
};

/**
 * A ONE-TIME membership purchase — an order, exactly like a booking's.
 */
export function openOneTimePaymentSheet({
  checkout,
  planName,
  payer,
}: MembershipSheet<OrderCheckout>): Promise<CheckoutOutcome> {
  return openSheet({
    key: checkout.key,
    order_id: checkout.orderId,
    amount: checkout.amount,
    currency: checkout.currency,
    name: MERCHANT_NAME,
    description: planName,
    prefill: payer,
  });
}

/**
 * A RECURRING membership — what the user authorizes is a standing mandate, not a
 * payment, so the options carry a subscription and NO amount: Razorpay takes the
 * part-month addon off the subscription itself, and an amount we invented here
 * would either be ignored or be wrong.
 */
export function openRecurringPaymentSheet({
  checkout,
  planName,
  payer,
}: MembershipSheet<SubscriptionCheckout>): Promise<CheckoutOutcome> {
  return openSheet({
    key: checkout.key,
    subscription_id: checkout.subscriptionId,
    name: MERCHANT_NAME,
    description: planName,
    prefill: payer,
  });
}

/**
 * A membership checkout of EITHER shape — the dispatch between the two above.
 *
 * Which one it is comes off the BLOCK, not off the plan's `isRecurring`: the
 * block is the thing being opened, and a checkout resumed from a stored
 * `checkoutPayload` has nothing else to go on.
 */
export function openMembershipSheet({
  checkout,
  planName,
  payer,
}: MembershipSheet<MembershipCheckout>): Promise<CheckoutOutcome> {
  return 'subscriptionId' in checkout
    ? openRecurringPaymentSheet({ checkout, planName, payer })
    : openOneTimePaymentSheet({ checkout, planName, payer });
}

/**
 * The SDK's options, as we build them — snake_case, the SDK's own spelling.
 *
 * Declared here rather than taken from `@types/react-native-razorpay`, whose
 * `CheckoutOptions` makes `amount`, `currency` and `order_id` REQUIRED. A
 * subscription checkout has none of the three, so the package's type can't
 * describe half the calls in this file; it's cast to at the single `open` below.
 */
type SheetOptions = {
  key: string;
  name: string;
  description: string;
  order_id?: string;
  subscription_id?: string;
  amount?: number;
  currency?: string;
  prefill?: { name?: string; email?: string; contact?: string };
};

/**
 * The sheet itself — one place opens it, so one place reads what comes back.
 * Everything the docstrings above say about outcomes is decided in here.
 */
async function openSheet(options: SheetOptions): Promise<CheckoutOutcome> {
  try {
    // The cast is explained on `SheetOptions`.
    await loadSdk().open(options as CheckoutOptions);

    // Resolved. The payment id it hands back is deliberately dropped: our
    // server hears about the capture from Razorpay directly, and this promise
    // resolving is not the same event.
    return { status: 'submitted' };
  } catch (caught) {
    // Logged whole and raw, because everything below this line is a lossy
    // reading of it — and because the shape is undocumented enough that the real
    // object is what you'll want when something surprises us.
    console.debug('Razorpay checkout failed', caught);

    const envelope = readEnvelope(caught);

    if (isCancellation(caught, envelope)) return { status: 'cancelled' };

    const reason = readString(envelope?.reason);
    if (reason && UNRESOLVED_REASONS.has(reason))
      return { status: 'submitted' };

    const error = toPaymentError(caught, envelope);
    reportPaymentFailure(caught, envelope, error);
    throw error;
  }
}

/**
 * Classify a rejection that isn't a cancellation.
 *
 * The NESTED description is Razorpay's own text about a payment, written for the
 * person who tried to pay ("The customer does not have sufficient funds in the
 * account") — the same rule [[apiError]] applies to a 4xx, so it's shown
 * verbatim and `reason` travels as the code. Note this also passes through the
 * odd integration failure whose text is aimed at us rather than the user
 * (`order_amount_mismatch` and friends); those are our bug to fix, they're
 * logged above, and a curated blocklist would be guessing at strings we haven't
 * seen from the live gateway.
 *
 * With NO envelope there's nothing user-facing in the payload at all, so it
 * comes down to what the platform says outside one: the web shim's tag, or the
 * numeric code — which is read per platform, since the two natives disagree.
 */
function toPaymentError(caught: unknown, envelope: Envelope | null) {
  // Web, checkout.js never loaded: no sheet was shown and no payment was
  // attempted, so there's no envelope and never will be. A script that won't
  // fetch is the connection often enough — and the network message ("check your
  // connection and try again") is the right advice for the rest of the causes
  // too, since an ad blocker or a proxy is also fixed by retrying elsewhere.
  if (readWebOutcome(caught) === 'unavailable') {
    return new PSErrorClass({
      kind: 'network',
      message: NETWORK_MESSAGE,
      details: caught,
    });
  }

  const description = readString(envelope?.description);
  if (description) {
    return new PSErrorClass({
      kind: 'api',
      message: description,
      code: readString(envelope?.reason),
      details: caught,
    });
  }

  // Native only, and named per platform: 0 is a network error on iOS, 2 is one
  // on Android, and neither number means anything on web — the shim carries no
  // numeric code, and the two web outcomes that could reach here are both
  // handled above. Spelt out rather than left as an `else` so web can't quietly
  // inherit Android's table if the shapes ever shift.
  const code = readNumber(readField(caught, 'code'));
  const isNetwork =
    Platform.OS === 'ios'
      ? code === 0
      : Platform.OS === 'android' && code === 2;

  return new PSErrorClass({
    kind: isNetwork ? 'network' : 'unknown',
    message: isNetwork ? NETWORK_MESSAGE : UNKNOWN_MESSAGE,
    details: caught,
  });
}

/**
 * Report the payment failures that are OURS, and stay quiet about the rest.
 *
 * Two whole categories never reach here, which is the point: a cancellation and
 * an unresolved-but-pending payment both RETURN above, so everything this sees
 * already failed. Of those, two more are dropped:
 *
 *   - 'network' goes for the same reason [[apiError]] drops it — the user's
 *     connection, or a web `checkout.js` an ad blocker ate. Not actionable, and
 *     it would be the highest-volume payment event we have.
 *   - `source === 'customer'` is a DECLINE: insufficient funds, a wrong OTP, a
 *     bank refusing 3DS. An expected business outcome rather than a bug, and the
 *     one that would drown everything else. Decline RATE belongs in a metrics
 *     dashboard, not error tracking.
 *
 * What's left is what `toPaymentError` calls our bug to fix — Razorpay saying the
 * request itself was wrong, `order_amount_mismatch` and friends, which come back
 * as `source: 'business'` — plus bank and gateway outages, plus the case worth
 * the most: no readable envelope at all, meaning the SDK handed us a shape this
 * file doesn't understand and the user got generic copy for it.
 *
 * Branching on `source` rather than on a list of `reason` strings is deliberate.
 * `toPaymentError` notes that a curated blocklist would be guessing at strings we
 * haven't seen from the live gateway; `source` is Razorpay's own answer to whose
 * fault it was, so it needs no guessing. A missing `source` REPORTS — a payload
 * we can't classify is exactly the thing worth hearing about.
 */
function reportPaymentFailure(
  caught: unknown,
  envelope: Envelope | null,
  error: PSErrorClass
): void {
  if (error.kind === 'network') return;

  const source = readString(envelope?.source);
  if (source === 'customer') return;

  const reason = readString(envelope?.reason);

  Sentry.captureException(error, {
    // Every throw from this file shares one stack — `toPaymentError` — so the
    // default grouping would file them all as a single issue. With no envelope
    // the PLATFORM is the discriminant instead: an unreadable iOS payload and an
    // unreadable Android one are different bugs in different bridges.
    fingerprint: envelope
      ? ['razorpay', source ?? 'no-source', reason ?? 'no-reason']
      : ['razorpay', 'unreadable-payload', Platform.OS],
    tags: {
      razorpay_source: source ?? 'none',
      razorpay_reason: reason ?? 'none',
      razorpay_step: readString(envelope?.step) ?? 'none',
    },
    // The raw object, because everything this file derives from it is a lossy
    // reading of it. Passed whole rather than stringified: Sentry's normalizer
    // handles the depth, and it's the payload you'll actually want when a shape
    // surprises us.
    extra: { payload: caught },
  });
}

/**
 * Did the user back out?
 *
 * Four tells, in order of trustworthiness:
 *
 *   0. The web shim's own tag. `modal.ondismiss` is unambiguous — Razorpay's
 *      web checkout fires it for the close button, the backdrop and Escape, and
 *      for nothing else — so the shim states the outcome outright rather than
 *      leaving a shape to be interpreted. Nothing to guess at, hence first.
 *   1. `reason === 'payment_cancelled'` — the same on both platforms, and what
 *      the checkout web layer sends for a dismiss on Android.
 *   2. iOS code 2 — the platform's own cancel code, used when the dismiss
 *      carries no error object at all. Guarded by `Platform.OS` because 2 is
 *      NETWORK_ERROR on Android.
 *   3. The raw `description`, for two Android cases where the envelope is
 *      missing: a hardware back-press with Razorpay's confirm-alert disabled
 *      sends the literal `"BackPressed"`, and the library drops the nested error
 *      object when its `metadata` is absent — but the description still holds
 *      the JSON text, so the reason is findable in it.
 *   4. Android backing out of the bank/OTP page mid-authentication: the SDK
 *      doesn't fire a clean cancel there either — it rejects with
 *      `reason: 'payment_error'`, `step: 'payment_authentication'`, and a nested
 *      `description` that is the literal STRING `"undefined"` (not a real
 *      message). Reading that string as user-facing text is what used to put
 *      the word "undefined" in a toast; it's actually this SDK's spelling of
 *      "the user left".
 *
 * Every branch errs toward "cancelled" on purpose: calling a real failure a
 * cancellation costs the user one retry, while calling a cancellation a failure
 * accuses them of something that didn't happen.
 */
function isCancellation(caught: unknown, envelope: Envelope | null): boolean {
  if (readWebOutcome(caught) === 'cancelled') return true;

  if (readString(envelope?.reason) === 'payment_cancelled') return true;

  if (Platform.OS === 'ios' && readNumber(readField(caught, 'code')) === 2) {
    return true;
  }

  const description = readString(readField(caught, 'description'));
  if (Platform.OS === 'android' && description) {
    if (description === 'BackPressed') return true;
    // Matched narrowly against the JSON text rather than sniffing for "cancel"
    // anywhere — a declined card's description mentions cancellation too.
    if (/"reason"\s*:\s*"payment_cancelled"/.test(description)) return true;
  }

  if (
    Platform.OS === 'android' &&
    readString(envelope?.reason) === 'payment_error' &&
    readString(envelope?.step) === 'payment_authentication' &&
    readString(envelope?.description) === 'undefined'
  ) {
    return true;
  }

  return false;
}

/**
 * The inside of Razorpay's error envelope, every field `unknown`. It's data off
 * a native bridge whose declared types are wrong in four places, so nothing here
 * is trusted — the readers below check each field. Same posture as
 * [[apiError]]'s `ErrorEnvelope`.
 */
type Envelope = {
  code?: unknown;
  description?: unknown;
  reason?: unknown;
  source?: unknown;
  step?: unknown;
  metadata?: unknown;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readField = (value: unknown, key: string): unknown =>
  isObject(value) ? value[key] : undefined;

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const readNumber = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

/**
 * The web shim's `webCheckout` tag — `cancelled`, `failed` or `unavailable`,
 * and null for anything off a native bridge, which never sets it. Read like
 * every other field here rather than imported as a type from
 * [[razorpay.web]]: that module only exists on web, so on native the import
 * would resolve to the real package, which declares nothing of the sort.
 *
 * `failed` isn't listed anywhere below because it needs no branch — the shim
 * puts Razorpay's envelope at `error`, the same key Android uses, so
 * `readEnvelope` finds it and the description, reason and pending-reason paths
 * all apply unchanged.
 */
function readWebOutcome(caught: unknown): string | null {
  return readString(readField(caught, 'webCheckout'));
}

/** Android nests the envelope at `error`, iOS at `details.error`. */
function readEnvelope(caught: unknown): Envelope | null {
  const direct = readField(caught, 'error');
  if (isObject(direct)) return direct;

  const nested = readField(readField(caught, 'details'), 'error');
  if (isObject(nested)) return nested;

  return null;
}

type CheckoutSdk = typeof import('react-native-razorpay').default;

/**
 * Load the SDK at CALL time, not at import time.
 *
 * `react-native-razorpay` builds a `NativeEventEmitter` over the native module
 * at module scope, so a static import runs that the moment any screen importing
 * this file is evaluated — including on web, and including on a screen that
 * never opens the sheet. Requiring it here keeps the native module untouched
 * until someone actually pays.
 *
 * On web this resolves to [[razorpay.web]] instead, via the alias in
 * `metro.config.js` — the reason there's no platform check in front of it. The
 * lazy require earns its keep there too: the shim injects a `<script>` tag, and
 * that shouldn't happen on a page nobody's paying from.
 *
 * The cast is to the NATIVE package's types either way. The shim stands in for
 * the same `open(options)`, and typing this against a module that doesn't exist
 * on two of the three platforms would buy nothing — the rejection shape, which
 * is the part that actually differs, is validated field by field above.
 */
function loadSdk(): CheckoutSdk {
  // Deliberate: a static import would evaluate the native module on every
  // platform at import time, which is the whole thing this function avoids.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return (require('react-native-razorpay') as { default: CheckoutSdk }).default;
}
